import type { ValidationResult } from './useSystemCheck';
import { logger } from 'modules/logger';
import analytics from 'modules/analytics';
import { globalContext } from 'modules/analytics/constants';
import { detectGpuCapability } from './gpuCapability';
import { detectHardwareAcceleration } from './hardwareAcceleration';

/** Enum-like object of all system check validators */
export const SysChkValidator = Object.freeze({
  ExtensionInstallState: 'extensionInstallState',
  ExtensionMinVersion: 'extensionMinVersion',
  HardwareAcceleration: 'hardwareAcceleration',
  GpuCapability: 'gpuCapability',
});
export type SysChkValidatorTypes = (typeof SysChkValidator)[keyof typeof SysChkValidator];

/** Enum-like object of enforcement level of each validator */
export const SysChkValidatorEnforcement = Object.freeze({
  /** Required for the app to function */
  Blocking: 'Blocking',
  Recommended: 'Recommended',
});
export type SysChkValidatorEnforcementTypes =
  (typeof SysChkValidatorEnforcement)[keyof typeof SysChkValidatorEnforcement];

type ValidatorOptions = {
  /** Force a validator result for testing purposes */
  force?: boolean;
};
type SystemCheckValidator = {
  enabled: boolean;
  enforcement: SysChkValidatorEnforcementTypes;
  validationFn: (options?: Nullable<ValidatorOptions>) => ValidationResult;
};

export const validateExtensionInstallState = (
  options: ValidatorOptions | null = {}
): ValidationResult => {
  const validatorCondition = window.__SIRONA_WORKSPACE_MANAGER_AVAILABLE__ != null;

  if (!validatorCondition) {
    logger.info(`[SysCheckFailure] Extension not installed`);
  }
  if ((options?.force === undefined && validatorCondition) || options?.force === true) {
    return {
      id: SysChkValidator.ExtensionInstallState,
      result: true,
      reason: 'Extension installed',
    };
  }
  return {
    id: SysChkValidator.ExtensionInstallState,
    result: false,
    reason: `Extension is not installed`,
  };
};

export const EXTENSION_MIN_VERSION = 3;
export const validateExtensionMinVersion = (
  options: ValidatorOptions | null = {}
): ValidationResult => {
  const validatorCondition =
    window.__SIRONA_WORKSPACE_MANAGER_API_VERSION__ != null &&
    window.__SIRONA_WORKSPACE_MANAGER_API_VERSION__ >= EXTENSION_MIN_VERSION;
  if (!validatorCondition) {
    logger.info(
      `[SysCheckFailure] Extension API version is out of date; actual: ${window.__SIRONA_WORKSPACE_MANAGER_API_VERSION__}, expected: >=${EXTENSION_MIN_VERSION} - ext version: ${window.__SIRONA_WORKSPACE_MANAGER_SEMVER__}`
    );
  }

  analytics.addContext(
    globalContext.workspace.extensionVersion,
    window.__SIRONA_WORKSPACE_MANAGER_SEMVER__
  );

  if ((options?.force === undefined && validatorCondition) || options?.force === true) {
    return {
      id: SysChkValidator.ExtensionMinVersion,
      result: true,
      reason: 'Extension version is up-to-date',
    };
  }

  return {
    id: SysChkValidator.ExtensionMinVersion,
    result: false,
    reason: `Installed extension version is out of date`,
  };
};

export const validateHardwareAcceleration = (
  options: ValidatorOptions | null = {}
): ValidationResult => {
  const result = detectHardwareAcceleration();
  // `unknown` (no WebGL info available) fails open.
  const validatorCondition = result.status !== 'disabled';

  if (!validatorCondition) {
    logger.info(
      `[SysCheckFailure] Hardware acceleration disabled; renderer: "${result.renderer}", reason: "${result.softwareRendererMatch ?? 'unknown software renderer'}"`
    );
  }

  if ((options?.force === undefined && validatorCondition) || options?.force === true) {
    return {
      id: SysChkValidator.HardwareAcceleration,
      result: true,
      reason: 'Hardware acceleration enabled',
      metadata: {
        renderer: result.renderer,
        vendor: result.vendor,
        glVersion: result.glVersion,
      },
    };
  }

  return {
    id: SysChkValidator.HardwareAcceleration,
    result: false,
    reason: 'Hardware acceleration disabled',
    metadata: {
      renderer: result.renderer,
      vendor: result.vendor,
      glVersion: result.glVersion,
      softwareRendererMatch: result.softwareRendererMatch,
    },
  };
};

export const validateGpuCapability = (options: ValidatorOptions | null = {}): ValidationResult => {
  // When hardware acceleration is off, the renderer string reports a software
  // rasterizer (SwiftShader / llvmpipe / etc.). The GPU denylist is then
  // meaningless — short-circuit so the user only sees one warning.
  const accel = detectHardwareAcceleration();
  if (accel.status === 'disabled') {
    return {
      id: SysChkValidator.GpuCapability,
      result: true,
      reason: 'GPU check skipped (hardware acceleration disabled)',
      metadata: { renderer: accel.renderer, vendor: accel.vendor, glVersion: accel.glVersion },
    };
  }

  const result = detectGpuCapability();
  // `unknown` (no debug info available) fails open — better than locking out
  // users on browsers/configs that gate UNMASKED_RENDERER_WEBGL.
  const validatorCondition = result.status !== 'unsupported';

  if (!validatorCondition) {
    logger.info(
      `[SysCheckFailure] GPU on denylist; renderer: "${result.renderer}", vendor: "${result.vendor}", glVersion: "${result.glVersion}", reason: "${result.denylistMatch ?? 'unknown'}"`
    );
  }

  if ((options?.force === undefined && validatorCondition) || options?.force === true) {
    return {
      id: SysChkValidator.GpuCapability,
      result: true,
      reason: 'GPU supported',
      metadata: {
        renderer: result.renderer,
        vendor: result.vendor,
        glVersion: result.glVersion,
      },
    };
  }

  return {
    id: SysChkValidator.GpuCapability,
    result: false,
    reason: 'Unsupported GPU hardware detected',
    metadata: {
      renderer: result.renderer,
      vendor: result.vendor,
      glVersion: result.glVersion,
      denylistMatch: result.denylistMatch,
    },
  };
};

export type SystemCheckValidatorsMap = Map<SysChkValidatorTypes, SystemCheckValidator>;
/** Map of all validators and their validation functions for evaluating the Sirona System Check
 * value */
export const SystemCheckValidators: SystemCheckValidatorsMap = new Map([
  [
    SysChkValidator.ExtensionInstallState,
    {
      enabled: true,
      enforcement: SysChkValidatorEnforcement.Blocking,
      validationFn: validateExtensionInstallState,
    },
  ],
  [
    SysChkValidator.ExtensionMinVersion,
    {
      enabled: true,
      enforcement: SysChkValidatorEnforcement.Blocking,
      validationFn: validateExtensionMinVersion,
    },
  ],
  [
    SysChkValidator.HardwareAcceleration,
    {
      enabled: true,
      enforcement: SysChkValidatorEnforcement.Recommended,
      validationFn: validateHardwareAcceleration,
    },
  ],
  [
    SysChkValidator.GpuCapability,
    {
      enabled: true,
      enforcement: SysChkValidatorEnforcement.Recommended,
      validationFn: validateGpuCapability,
    },
  ],
]);

/** Runs all enabled validators to produce a single result. Only `Blocking`
 *  validators contribute to the overall `result`; `Recommended` validators are
 *  always run and included in `validators` but a failure does not set the
 *  overall result to `false`. */
export const validateAll = (
  options: ValidatorOptions | null = {}
): {
  result: boolean;
  validators: ValidationResult[];
} => {
  const entries = Array.from(SystemCheckValidators.entries()).filter(([, v]) => v.enabled);
  const results = entries.map(([, v]) => v.validationFn(options));

  const result =
    options?.force !== undefined
      ? options.force
      : entries.every(
          ([, v], i) => v.enforcement !== SysChkValidatorEnforcement.Blocking || results[i].result
        );

  return { result, validators: results };
};

/** Run a single validator by ID */
export const validateSingle = (
  validatorId: SysChkValidatorTypes,
  options: ValidatorOptions | null = {}
):
  | ValidationResult
  | (Omit<ValidationResult, 'id'> & {
      id: null;
    }) => {
  const validator = SystemCheckValidators.get(validatorId);
  if (validator == null) {
    logger.error(`Validator "${validatorId}" not found`);
    return { id: null, result: false, reason: undefined };
  }
  return validator?.validationFn(options);
};
